import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import './verify2fa.css';

const Verify2FA = () => {
  const navigate = useNavigate();
  const { verify2FA, checkSession, logout, isLoading } = useAuth();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [useBackupCode, setUseBackupCode] = useState(false); // ✅ Toggle state

  useEffect(() => {
    const checkAuth = async () => {
      const session = await checkSession();
      if (!session.authenticated) {
        navigate('/');
      }
    };
    checkAuth();
  }, [checkSession, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const minLength = useBackupCode ? 8 : 6;
    if (!token || token.length < minLength) {
      setError(`Please enter a valid ${useBackupCode ? 'backup code' : '6-digit code'}`);
      return;
    }

    try {
      const result = await verify2FA(token);

      if (result.success) {
        console.log('✅ 2FA verified successfully');
        navigate('/usergate');
      } else {
        setAttempts(attempts + 1);
        setError(result.error || 'Invalid verification code');
        setToken('');
        
        if (attempts >= 2) {
          setError('Too many failed attempts. Please try logging in again.');
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
      setToken('');
    }
  };

  const handleTokenChange = (e) => {
    const value = e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase(); // ✅ Allow letters for backup codes
    const maxLength = useBackupCode ? 8 : 6;
    if (value.length <= maxLength) {
      setToken(value);
    }
  };

  const handleCancel = async () => {
    // Logout completely - destroy session
    await logout();
  };

  const toggleInputType = () => {
    setUseBackupCode(!useBackupCode);
    setToken('');
    setError('');
  };

  return (
    <div className="verify2fa-container">
      <div className="verify2fa-card">
        <div className="verify2fa-header">
          <div className="lock-icon">🔐</div>
          <h1>Two-Factor Authentication</h1>
          <p>
            {useBackupCode 
              ? 'Enter one of your 8-character backup codes'
              : 'Enter the 6-digit code from your authenticator app'
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="verify2fa-form">
          <div className="code-input-wrapper">
            <input
              type="text"
              inputMode={useBackupCode ? 'text' : 'numeric'}
              pattern={useBackupCode ? '[A-Z0-9]*' : '[0-9]*'}
              value={token}
              onChange={handleTokenChange}
              placeholder={useBackupCode ? 'XXXXXXXX' : '000000'}
              className="code-input"
              autoFocus
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="verify2fa-actions">
            <button
              type="submit"
              className="verify-button"
              disabled={isLoading || token.length < (useBackupCode ? 8 : 6)}
            >
              {isLoading ? (
                <>
                  <span className="spinner-small"></span>
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className="cancel-button"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>

          {/* ✅ Toggle Button */}
          <button
            type="button"
            onClick={toggleInputType}
            className="toggle-input-button"
            disabled={isLoading}
          >
            {useBackupCode 
              ? '← Use authenticator code instead'
              : '🔑 Use backup code instead'
            }
          </button>
        </form>

        <div className="verify2fa-help">
          <details>
            <summary>Need help?</summary>
            <div className="help-content">
              <p><strong>Lost your device?</strong></p>
              <p>Click "Use backup code" above to enter one of your 8-character backup codes.</p>
              <p><strong>Can't access your codes?</strong></p>
              <p>Contact support for assistance recovering your account.</p>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export default Verify2FA;